import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { Tooltip, type TooltipRefProps } from "react-tooltip";
import { t } from "ttag";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import iconQuestionMarkCircleFilled from "../../../../img/icons/question-mark-circle-filled.svg";

import styles from "./index.module.scss";

function getItemsLabel(qty: number): string {
    // TODO(i18n): Use ngettext for plurals: ngettext(msgid`item`, `items`, qty)
    return qty === 1 ? "item" : "items";
}

function buildCartReferenceNumber(
    hideBasketID: boolean,
    basketID: string | undefined,
    phoneLink: string | null,
    phoneNumber: string | null,
    isMobile: boolean,
    isKeyDown: boolean,
    keyDownHandler: (event: React.KeyboardEvent<HTMLElement>) => void,
    setIsKeyDown: (value: boolean) => void,
    tooltipRef: React.Ref<TooltipRefProps>,
): React.ReactNode {
    if (hideBasketID) {
        return null;
    }
    const tipId = "basket-lines__tooltip-cart-ref";
    return (
        <div className="basket-lines__header-cart-ref">
            <div className="basket-lines__header-cart-ref--first-line">
                {t`Cart Reference Number:`}
            </div>
            <div className="basket-lines__header-cart-ref--second-line">
                <span className="basket-lines__header-cart-ref--bold">
                    {basketID}
                </span>
                {phoneLink && phoneNumber && (
                    <div className="tooltip-container">
                        <div
                            tabIndex={0}
                            className="basket-lines__header-cart-ref-icon-container"
                            data-tooltip-id={tipId}
                            title={t`Have an issue completing your order?`}
                            onKeyDown={keyDownHandler}
                            onMouseOver={() => {
                                setIsKeyDown(false);
                            }}
                        >
                            <SVG
                                aria-hidden="true"
                                className="basket-lines__header-cart-ref-icon"
                                src={iconQuestionMarkCircleFilled}
                            />
                            <span className="ada-screenreader-only">
                                {/* TODO(i18n): Use t tagged template: {t`Open Tooltip`} */}
                                Open Tooltip
                            </span>
                        </div>
                        <Tooltip
                            ref={tooltipRef}
                            className="basket-lines__tooltip basket-lines__tooltip--cart-ref tooltip"
                            clickable={true}
                            delayHide={200}
                            globalCloseEvents={{ escape: true, scroll: true }}
                            id={tipId}
                            place={isMobile ? "left" : "bottom"}
                        >
                            <strong>
                                {t`Have an issue completing your order?`}
                            </strong>
                            <br />
                            {richT(
                                t`Call <PhoneNumber /> and provide your cart ID for assistance.`,
                                {
                                    PhoneNumber: () => <>{phoneNumber}</>,
                                },
                            )}
                        </Tooltip>
                    </div>
                )}
            </div>
        </div>
    );
}

export const BasketHeader = (
    props: Immutable<{
        hideBasketID: boolean;
        phoneLink: string | null;
        phoneNumber: string | null;
        basketID?: string;
        isMobile: boolean;
        totalItems: number;
    }>,
): React.ReactNode => {
    const [isKeyDown, setIsKeyDown] = React.useState(false);
    const tooltipRef = React.useRef<TooltipRefProps>(null);

    const keyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== "Enter") {
            return;
        }
        if (!isKeyDown) {
            setIsKeyDown(true);
            return;
        }
        tooltipRef.current?.close();
    };

    return (
        <header className={styles.header}>
            <h1 className={styles.title}>
                {/* TODO(i18n): Use ngettext to keep count and item label together: {ngettext(msgid`Your Cart (${props.totalItems} item)`, `Your Cart (${props.totalItems} items)`, props.totalItems)} */}
                {t`Your Cart`}{" "}
                <span className={styles.itemNumber}>
                    ({props.totalItems} {getItemsLabel(props.totalItems)})
                </span>
            </h1>
            {buildCartReferenceNumber(
                props.hideBasketID,
                props.basketID,
                props.phoneLink,
                props.phoneNumber,
                props.isMobile,
                isKeyDown,
                keyDownHandler,
                setIsKeyDown,
                tooltipRef,
            )}
        </header>
    );
};
